import DateFnsUtils from "@date-io/date-fns";
import {
  Button,
  FormControlLabel,
  Grid,
  Hidden,
  InputLabel,
  OutlinedInput,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import clsx from "clsx";
import "date-fns";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../auth/actions/logout.actions";
import { DropDown } from "../../../common/DropDown";
import { UpdateParticipantEmail } from "../../../common/update-participant-email";
import { Profile } from "../../../models/profile";
import {
  ErrorMessages,
  SwitchWithTooltip,
  TextMask,
} from "../../../shared/components";
import { CropImage } from "../../../shared/components/CropImage";
import { formattedDate } from "../../../shared/formattedDate";
import { useHistory } from "../../../shared/hooks/useHistory";
import { LogoutIcon } from "../../../shared/icons";
import { DateOfBirthIcon } from "../../../shared/icons/DateOfBirthIcon";
import { setClose } from "../../../shared/request/actions/requestActions";
import { RootState } from "../../../store";
import {
  asyncGetSmsNotificationSetting,
  asyncUpdateProfile,
  asyncUpdateSmsNotification,
  resetProfile,
  setProfilePicture,
} from "../../actions/profileActions";
import { CHILDREN, MARITAL_STATUS, STATES } from "../../consts";
import { useStyles } from "./profile-form.styles";
import { useInIframe } from "../../../shared/hooks";

const initialStateErrorsForm = [];

interface ProfileFormProps {
  profileState: Profile;
}

export const ProfileForm = ({ profileState }: ProfileFormProps) => {
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [errorsForm, setErrorsForm] = useState(initialStateErrorsForm);
  const [errorSecondaryEmail, setErrorSecondaryEmail] = useState(false);
  const [profile, setProfile] = useState(profileState);
  const [openRemoveParticipant, setOpenRemoveParticipant] = useState(false);
  const isInIframe = useInIframe();

  const { profileInformation, request, userProfileId, subscriptionStatus } =
    useSelector((state: RootState) => {
      return {
        profileInformation: state.profile,
        request: state.request.request,
        userProfileId: state.profile.participantProfileId,
        subscriptionStatus:
          state.auth.login.stripeParticipant.subscriptionStatus,
      };
    });

  useEffect(() => {
    setProfile(profileInformation);
  }, [profileInformation]);

  useEffect(() => {
    dispatch(asyncGetSmsNotificationSetting(userProfileId));
  }, [dispatch, userProfileId]);

  const updateSmsNotification = (event) => {
    const { checked } = event.target;
    dispatch(
      asyncUpdateSmsNotification({
        profileId: userProfileId,
        enabled: checked,
      })
    );
  };

  const resetErrors = () => {
    setErrorsForm(initialStateErrorsForm);
    setErrorSecondaryEmail(false);
  };

  const setErrorForm = (key, message) => {
    setErrorsForm((prevState) => {
      return [...prevState, { key, message }];
    });
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    if (name === "maritalStatusId") {
      setProfile({ ...profile, [name]: Number.parseInt(value, 10) });
    } else {
      setProfile({ ...profile, [name]: value });
    }
  };

  const handleBirthDate = (date) => {
    setProfile({ ...profile, dateOfBirth: date });
  };

  const handleChangeSpouse = (event) => {
    if (event.target.checked) {
      setProfile({ ...profile, hasSpouse: event.target.checked });
    } else {
      setProfile({
        ...profile,
        hasSpouse: event.target.checked,
        spouseAnnualIncome: 0,
        spouseRetirementAge: 0,
      });
    }
  };

  const handleChangeZipCode = (event) => {
    const re = /^[0-9\b-]+$/;
    const { name, value } = event.target;
    if (event.target.value === "" || re.test(event.target.value)) {
      setProfile({ ...profile, [name]: value });
    }
  };

  const handleImageUpload = (path) => {
    setProfile({ ...profile, profilePicturePath: path });
    dispatch(setProfilePicture(path));
  };

  const handleSubmit = async (event) => {
    resetErrors();
    event.preventDefault();
    let isValid = true;

    if (
      profile.secondaryEmail !== null &&
      !profile.secondaryEmail?.includes("@") &&
      !profile.secondaryEmail?.includes(".")
    ) {
      setErrorSecondaryEmail(true);
    }

    if (!profile.name) {
      setErrorForm("name", "First Name is required");
      isValid = false;
    }
    if (!profile.lastName) {
      setErrorForm("lastName", "Last Name is required");
      isValid = false;
    }
    if (profile.dateOfBirth?.toString() === "Invalid Date") {
      setErrorForm("dateOfBirth", "Date Of Birth is invalid");
      isValid = false;
    }

    if (profile.retirementDate?.toString() === "Invalid Date") {
      setErrorForm("retirementDate", "Retirement date is invalid");
      isValid = false;
    }

    if (Number.isNaN(Number(profile.inflation))) {
      setErrorForm("inflation", "Inflation Interest Rate must be a number");
      isValid = false;
    } else if (Number(profile.inflation) === 0) {
      setErrorForm(
        "inflation",
        "Inflation Interest Rate must be greater than 0"
      );
      isValid = false;
    }

    const formData = new FormData();
    const cloneData = { ...profile };

    Object.keys(cloneData).forEach((key) => {
      if (key === "dateOfBirth" || key === "retirementDate") {
        if (cloneData[key] !== null && isValid) {
          cloneData[key] = formattedDate(cloneData[key]);
        }
      }
      if (cloneData[key] !== null && isValid)
        formData.append(key, cloneData[key]);
    });

    if (isValid) {
      await dispatch(asyncUpdateProfile(formData));

      setTimeout(() => {
        dispatch(setClose());
        if (isInIframe) {
          history.replace(`/participant/participantProfile`);
          return;
        }
        history.replace(`/participant/dashboard`);
      }, 2000);
    }
  };

  const handleClose = () => {
    dispatch(setClose());
  };

  const handleLogout = () => {
    dispatch(resetProfile());
    dispatch(logout());
  };

  const handleCancelButton = () => {
    history.goBack();
  };

  const contact = () => {
    return (
      <>
        <Grid item xs={12}>
          <Typography
            variant="h4"
            color="textSecondary"
            align="left"
            className={classes.title}
          >
            Contact
          </Typography>
          <hr />
        </Grid>
        <Grid item xs={12} className={classes.numbers}>
          <InputLabel
            variant="standard"
            htmlFor="workPhone"
            className={classes.inputField}
          >
            Work Phone
          </InputLabel>
          <OutlinedInput
            fullWidth
            value={profile.workPhone || ""}
            onChange={handleFormChange}
            name="workPhone"
            id="workPhone"
            inputComponent={TextMask}
            className={classes.fontColor}
          />
        </Grid>
        <Grid item md={4} xs={12} className={classes.numbers}>
          <InputLabel
            variant="standard"
            htmlFor="mobile"
            className={classes.inputField}
          >
            Mobile Phone
          </InputLabel>
          <OutlinedInput
            fullWidth
            value={profile.mobile || ""}
            onChange={handleFormChange}
            name="mobile"
            id="mobile"
            inputComponent={TextMask}
            className={classes.fontColor}
          />
        </Grid>
        <Grid item md={3} xs={12} className={classes.paper}>
          <SwitchWithTooltip
            tooltipText="Enable or disable SMS Notifications"
            value={profile.smsSettingEnable}
            checked={profile.smsSettingEnable}
            disabled={request.isFetching}
            classNameFormControl={classes.smsNotification}
            classNameSwitch={classes.switch}
            classNameFormGroup={classes.formGroup}
            classNameLabel={classes.labelToggle}
            text="Receive SMS Notifications"
            onChange={updateSmsNotification}
            component={undefined}
            position="start"
          />
        </Grid>
        <Grid item md={4} xs={12} className={classes.paper}>
          <Tooltip title="Update your email">
            <TextField
              onClick={() => setOpenRemoveParticipant(true)}
              onChange={() => false}
              variant="outlined"
              type="email"
              margin="normal"
              fullWidth
              label="Primary Email"
              name=""
              id=""
              value={profile.email || ""}
              className={clsx(
                classes.primaryEmail,
                classes.textField,
                classes.textFieldContact
              )}
              inputProps={{
                className: classes.fontColor,
                "data-testid": "primary-email",
              }}
            />
          </Tooltip>
        </Grid>
        <Grid
          item
          md={5}
          sm={7}
          xs={12}
          className={clsx(classes.paper, classes.widthItems)}
        >
          <TextField
            variant="outlined"
            type="email"
            margin="normal"
            fullWidth
            error={errorSecondaryEmail}
            helperText={errorSecondaryEmail && "The email is not correct."}
            label="Secondary Email"
            name="secondaryEmail"
            id="secondaryEmail"
            value={profile.secondaryEmail || ""}
            className={clsx(classes.textField, classes.secondaryEmail)}
            inputProps={{
              className: classes.fontColor,
            }}
            onChange={handleFormChange}
          />
        </Grid>
        <Grid
          item
          md={8}
          xs={12}
          className={clsx(classes.paper, classes.paddingForContact)}
        >
          <TextField
            variant="outlined"
            margin="normal"
            fullWidth
            label="Address"
            name="address"
            id="address"
            value={profile.address || ""}
            className={clsx(classes.textField, classes.textFieldContact)}
            inputProps={{
              className: classes.fontColor,
            }}
            onChange={handleFormChange}
          />
        </Grid>
        <Grid
          className={clsx(classes.paper, classes.paddingForContact)}
          item
          md={3}
          xs={12}
        >
          <TextField
            variant="outlined"
            margin="normal"
            fullWidth
            label="City"
            name="city"
            id="city"
            value={profile.city || ""}
            onChange={handleFormChange}
            className={clsx(classes.textField, classes.textFieldContact)}
            inputProps={{
              className: classes.fontColor,
            }}
          />
        </Grid>
        <Grid
          item
          md={3}
          xs={12}
          className={clsx(classes.paper, classes.paddingForContact)}
        >
          <DropDown
            data={STATES}
            label="State"
            name="state"
            state={profile.state ?? "0"}
            onChange={handleFormChange}
            classNameLabel={classes.inputLabelDropdown}
            classNameSelect={classes.textFieldLato}
          />
        </Grid>
        <Grid
          item
          md={2}
          xs={12}
          className={clsx(classes.paper, classes.paddingForContact)}
        >
          <TextField
            variant="outlined"
            margin="normal"
            fullWidth
            label="Zip Code"
            name="zipCode"
            id="zipCode"
            value={profile.zipCode || ""}
            onChange={handleChangeZipCode}
            className={clsx(classes.textField, classes.textFieldContact)}
            inputProps={{
              className: classes.fontColor,
            }}
          />
        </Grid>
      </>
    );
  };

  const childrenDropDown = () => {
    return (
      <Grid
        item
        md={3}
        sm={6}
        xs={12}
        className={clsx(classes.paper, classes.widthItems)}
      >
        <DropDown
          data={CHILDREN}
          label="Children"
          name="noChildren"
          state={profile.noChildren >= 0 ? String(profile.noChildren) : "0"}
          onChange={handleFormChange}
          classNameLabel={classes.inputLabelDropdown}
          classNameSelect={classes.textFieldLato}
        />
      </Grid>
    );
  };

  const cancelButton = () => {
    return (
      <Button
        onClick={handleCancelButton}
        color="secondary"
        variant="contained"
        fullWidth={false}
        className={classes.cancelButton}
        disabled={request.isFetching}
      >
        Cancel
      </Button>
    );
  };

  const saveButton = () => {
    return (
      <Button
        data-testid="btnSendData"
        type="submit"
        variant="contained"
        fullWidth={false}
        color="primary"
        className={classes.saveButton}
        disabled={request.isFetching}
      >
        Save
      </Button>
    );
  };

  const focusAreasLabels = () =>
    MARITAL_STATUS.filter((maritalStatus) => maritalStatus.id !== 0).map(
      ({ id, text }) => (
        <FormControlLabel
          key={id}
          value={id.toString()}
          control={<Radio color="default" />}
          label={text}
        />
      )
    );

  return (
    <>
      {!isInIframe ? (
        <Grid item md={12} className={classes.titleContainer}>
          <Typography
            component="h1"
            variant="h5"
            align="left"
            className={classes.mainTitle}
          >
            Profile
          </Typography>
          <Button
            onClick={handleLogout}
            className={classes.logoutButton}
            startIcon={<LogoutIcon className={classes.logOutIcon} />}
          >
            Log out
          </Button>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <ErrorMessages errorMessages={errorsForm} onClose={resetErrors} />
          <form className={classes.form} noValidate onSubmit={handleSubmit}>
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  variant="h4"
                  color="textSecondary"
                  align="left"
                  className={clsx(classes.title, classes.titleSeparator)}
                >
                  About Me
                </Typography>
                <hr />
              </Grid>
              <Grid item lg={7} md={6} xs={12} className={classes.paper}>
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  label="Organization Name"
                  name="organizationName"
                  id="organizationName"
                  value={profile.organizationName || ""}
                  autoFocus
                  disabled
                  className={classes.hide}
                />
                <TextField
                  variant="outlined"
                  margin="normal"
                  required={false}
                  className={classes.textField}
                  fullWidth
                  label="First Name"
                  name="name"
                  id="name"
                  inputProps={{
                    "data-testid": "name",
                    className: classes.fontColor,
                  }}
                  autoFocus
                  value={profile.name || ""}
                  onChange={handleFormChange}
                />
                <TextField
                  variant="outlined"
                  margin="normal"
                  required={false}
                  className={classes.textField}
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  id="lastName"
                  value={profile.lastName || ""}
                  onChange={handleFormChange}
                  inputProps={{
                    "data-testid": "last-name",
                    className: classes.fontColor,
                  }}
                />
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    ToolbarComponent={classes.toolBarCalendar as never}
                    inputVariant="outlined"
                    autoOk
                    clearable
                    views={["year", "month", "date"]}
                    variant="inline"
                    format="MM/dd/yyyy"
                    margin="normal"
                    label="Date Of Birth"
                    name="dateOfBirth"
                    id="dateOfBirth"
                    value={profile.dateOfBirth}
                    onChange={handleBirthDate}
                    KeyboardButtonProps={{
                      "aria-label": "change date Of Birth",
                    }}
                    className={clsx(classes.textField, classes.dateOfBirth)}
                    inputProps={{
                      "data-testid": "date-of-birth",
                      className: classes.fontColor,
                    }}
                    keyboardIcon={<DateOfBirthIcon />}
                  />
                </MuiPickersUtilsProvider>
                <TextField
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  label="Assigned Educator"
                  name="educatorId"
                  id="educatorId"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={profile.educatorId}
                  disabled
                  onChange={handleFormChange}
                  className={classes.hide}
                />
                <TextField
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  label="Employee ID"
                  name="employeeId"
                  id="employeeId"
                  value={profile.employeeId}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled
                  onChange={handleFormChange}
                  className={classes.hide}
                />
              </Grid>
              <Grid
                item
                lg={5}
                md={6}
                xs={12}
                className={clsx(classes.paper, classes.profilePicture)}
              >
                <CropImage
                  participantProfileId={profile.participantProfileId}
                  avatarParticipant={profile.profilePicturePath}
                  onUploadedImage={handleImageUpload}
                />
              </Grid>
              <Grid item md={4} xs={6} className={classes.paper}>
                <TextField
                  className={classes.hide}
                  variant="outlined"
                  type="email"
                  margin="normal"
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  id="email"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{ "data-testid": "email" }}
                  value={profile.email || ""}
                  disabled
                  onChange={handleFormChange}
                />
              </Grid>
              {<>{contact()}</>}

              <Grid item xs={12}>
                <Typography
                  variant="h4"
                  color="textSecondary"
                  align="left"
                  className={clsx(classes.title, classes.titleSeparator)}
                >
                  Household
                </Typography>
                <hr />
              </Grid>

              <Grid item md={3} sm={6} xs={12} className={classes.paper}>
                <Typography
                  variant="h4"
                  color="textSecondary"
                  align="left"
                  className={classes.maritalStatus}
                >
                  Marital Status
                </Typography>

                <RadioGroup
                  aria-label="maritalStatusId"
                  name="maritalStatusId"
                  value={profile.maritalStatusId?.toString()}
                  onChange={handleFormChange}
                  className={classes.fontColor}
                >
                  {focusAreasLabels()}
                </RadioGroup>
              </Grid>
              <Grid item xs={8} className={classes.widthItems}>
                {childrenDropDown()}
              </Grid>

              <Grid item md={3} sm={6} xs={12} className={classes.spouse}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={profile.hasSpouse}
                      onChange={handleChangeSpouse}
                      name="spouse"
                      color="primary"
                      className={classes.switch}
                    />
                  }
                  className={classes.label}
                  classes={{
                    label: classes.labelToggle,
                  }}
                  labelPlacement="start"
                  label="Spouse"
                />
              </Grid>
              <Grid
                item
                md={3}
                sm={6}
                xs={12}
                className={clsx(classes.paper, classes.spouseAnnualIncome)}
              >
                <TextField
                  key="spouseAnnualIncome"
                  type="number"
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  label="Spouse Annual Income"
                  name="spouseAnnualIncome"
                  id="spouseAnnualIncome"
                  value={
                    profile.spouseAnnualIncome >= 0
                      ? profile.spouseAnnualIncome
                      : ""
                  }
                  disabled={!profile.hasSpouse}
                  onChange={handleFormChange}
                  className={classes.annualIncomeAndRetirement}
                  inputProps={{
                    className: classes.fontColor,
                    "data-testid": "spouse-annual-income",
                  }}
                />
              </Grid>
              <Grid
                item
                md={3}
                sm={6}
                xs={12}
                className={classes.retirementAgeOfSpouse}
              >
                <TextField
                  type="number"
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  label="Retirement Age Of Spouse"
                  name="spouseRetirementAge"
                  id="spouseRetirementAge"
                  value={
                    profile.spouseRetirementAge >= 0
                      ? profile.spouseRetirementAge
                      : ""
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={!profile.hasSpouse}
                  onChange={handleFormChange}
                  className={classes.annualIncomeAndRetirement}
                  inputProps={{
                    className: classes.fontColor,
                    "data-testid": "spouse-retirement-age",
                  }}
                />
              </Grid>
              <Hidden only={["sm", "xs"]}>
                <Grid item lg={3} md={3}>
                  &nbsp;
                </Grid>
              </Hidden>
              <Grid item md={3} sm={6} xs={12} className={classes.buttons}>
                {cancelButton()}
                &nbsp;&nbsp;&nbsp;&nbsp;
                {saveButton()}
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Grid>
      {request.isOpen && (
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          open={request.isOpen}
          onClose={handleClose}
        >
          <Alert onClose={handleClose} severity={request.type}>
            {request.message}
          </Alert>
        </Snackbar>
      )}
      <UpdateParticipantEmail
        participant={profileInformation}
        subscriptionStatus={subscriptionStatus}
        close={() => setOpenRemoveParticipant(false)}
        open={openRemoveParticipant}
        onAfter={() => null}
      />
    </>
  );
};
